import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm, useWatch } from "react-hook-form";
import { useMemo } from "react";
import { formatNumber } from "@/lib/helper";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createMarketSetting } from "@/services/Merchant.service";
import { toast } from "react-toastify";

const addRateSchema = z.object({
  /** Pair `id` from GET market pairs response — sent as `supportedPairId` to the API */
  supportedPairId: z.string().min(1, "Select a currency pair"),
  buyingOffset: z.string().min(1, "Select buy offset"),
  sellingOffset: z.string().min(1, "Select sell offset"),
  buyingPriceMarkup: z.string().min(1, "Buy price markup is required"),
  sellingPriceMarkup: z.string().min(1, "Sell price markup is required"),
});

type AddRateFormValues = z.infer<typeof addRateSchema>;

const offsetOptions = [
  { value: "flat", label: "Flat" },
  { value: "manual", label: "Manual" },
  { value: "percentage", label: "Percentage" },
];

/** Shape returned by market pairs API (example) */
export type MarketPairOption = {
  id: string;
  base: string;
  quote: string;
  buyRate?: number | string;
  sellRate?: number | string;
  buyRateProvider?: string;
  sellRateProvider?: string;
};

function formatProviderLabel(provider: string | undefined): string {
  if (!provider?.trim()) return "—";
  const p = provider.trim();
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
}

/** Format rate for display (large fiat-style or precise crypto rates). */
function formatPairRate(rate: number | string | undefined | null): string {
  if (rate === undefined || rate === null || rate === "") return "—";
  const num = typeof rate === "string" ? parseFloat(rate) : rate;
  if (Number.isNaN(num)) return "—";
  const abs = Math.abs(num);
  const decimals = abs >= 1_000 ? 2 : abs >= 1 ? 4 : 8;
  return formatNumber(num, decimals);
}

type AddNewRateProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  pairs: MarketPairOption[];
  pairsLoading: boolean;
};

export default function AddNewRate({
  open,
  onOpenChange,
  onSuccess,
  pairs,
  pairsLoading,
}: AddNewRateProps) {
  const form = useForm<AddRateFormValues>({
    resolver: zodResolver(addRateSchema),
    defaultValues: {
      supportedPairId: "",
      buyingOffset: "",
      sellingOffset: "",
      buyingPriceMarkup: "15",
      sellingPriceMarkup: "10",
    },
  });

  const selectedPairId = useWatch({ control: form.control, name: "supportedPairId" });
  const selectedPair = useMemo(
    () => pairs.find((p) => p.id === selectedPairId) ?? null,
    [pairs, selectedPairId]
  );

  const buyProvider = formatProviderLabel(selectedPair?.buyRateProvider);
  const buyRateDisplay = formatPairRate(selectedPair?.buyRate);
  const sellProvider = formatProviderLabel(selectedPair?.sellRateProvider);
  const sellRateDisplay = formatPairRate(selectedPair?.sellRate);

  const onSubmit = async (data: AddRateFormValues) => {
    const pair = pairs.find((p) => p.id === data.supportedPairId);
    if (!pair?.id) {
      toast.error("Selected pair is not valid. Please choose a currency pair again.");
      return;
    }
    try {
      await createMarketSetting({
        supportedPairId: pair.id,
        offset: data.buyingOffset.toUpperCase(),
        sellingOffset: data.sellingOffset.toUpperCase(),
        buying: Number(data.buyingPriceMarkup),
        selling: Number(data.sellingPriceMarkup),
      });
      toast.success("Market rate added successfully");
      form.reset({
        supportedPairId: "",
        buyingOffset: "",
        sellingOffset: "",
        buyingPriceMarkup: "15",
        sellingPriceMarkup: "10",
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to add market rate"
      );
    }
  };

  return (
    <CustomModal open={open} onOpenChange={onOpenChange} size="2xl">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader className="text-center py-4 md:py-6 px-4 md:px-8 border-b bg-background rounded-t-[16px]">
            <DialogTitle className="text-lg font-semibold text-primary-3">
              Add new rate
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1   gap-y-5 px-4 md:px-8 py-4 md:py-6 max-h-[70vh] overflow-y-auto">
            <FormField
              control={form.control}
              name="supportedPairId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[18px] font-medium text-primary-3">
                    Currency pair
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-10 rounded-[8px] border-grey-300">
                        <SelectValue placeholder="select currency pair" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {pairsLoading ? (
                        <SelectItem value="loading" disabled>Loading pairs...</SelectItem>
                      ) : (
                        pairs.map((pair) => (
                          pair.base && pair.quote && (
                            <SelectItem key={pair.id} value={pair.id}>
                              {`${pair.base}/${pair.quote}`}
                            </SelectItem>
                          )
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />



            {/* <div className="space-y-1.5">
              <FormLabel className="text-sm font-medium text-primary-3">
                Currency rate
              </FormLabel>
              <p className="text-sm text-gray-70">rates will appear here</p>
            </div> */}



            <div className="col-span-1 md:col-span-2  ">
              <div className="grid grid-cols md:1 gap-4 bg-muted/50 border border-border p-4 rounded-[8px]">
                <div>
                  <p className="text-[18px] font-medium text-primary-3 ">Buy Setting</p>
                  <p className="mb-[14px] text-[12px] text-gray-80">
                    Provider: {selectedPair ? buyProvider : "—"}, Rate:{" "}
                    {selectedPair ? buyRateDisplay : "—"}
                  </p>
                  <FormField
                    control={form.control}
                    name="buyingOffset"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-primary-3">
                          Offset <span className="text-error">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-10 rounded-[8px] border-grey-300 bg-background">
                              <SelectValue placeholder="select offset" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {offsetOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="buyingPriceMarkup"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-primary-3">
                        Price Markup <span className="text-error">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          className="h-10 rounded-[8px] border-grey-300 bg-background"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />


              </div>
              <div className="grid grid-cols md:1 gap-4 mt-[30px] bg-muted/50 border border-border p-4 rounded-[8px]">
                <div>
                  <p className="text-[18px] font-medium text-primary-3 ">Sell Setting</p>
                  <p className="mb-[14px] text-[12px] text-gray-80">
                    Provider: {selectedPair ? sellProvider : "—"}, Rate:{" "}
                    {selectedPair ? sellRateDisplay : "—"}
                  </p>
                  <FormField
                    control={form.control}
                    name="sellingOffset"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-primary-3">
                          Offset <span className="text-error">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-10 rounded-[8px] border-grey-300 bg-background">
                              <SelectValue placeholder="select offset" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {offsetOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="sellingPriceMarkup"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-primary-3">
                        Price Markup <span className="text-error">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          className="h-10 rounded-[8px] border-grey-300 bg-background"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />


              </div>
            </div>
          </div>

          <DialogFooter className="px-4 md:px-8 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px] border-t">
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className="text-primary0 h-12 rounded-[10px] px-4 hover:text-primary0"
              >
                <ChevronLeft className="mr-2" />
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] px-4 text-white"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Add rate
                  <ChevronRight className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </CustomModal>
  );
}
